import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSpring, animated } from "@react-spring/web";
import { Check, ChevronDown } from "lucide-react";

export interface SelectOption {
  value: string;
  label: string;
}

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  disabled?: boolean;
  placeholder?: string;
  mono?: boolean;
}

export function CustomSelect({ value, onChange, options, disabled, placeholder, mono }: CustomSelectProps) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  function handleTriggerClick() {
    if (disabled) return;
    if (open) {
      setOpen(false);
    } else {
      if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect());
      setOpen(true);
    }
  }

  function handleSelect(val: string) {
    onChange(val);
    setOpen(false);
  }

  // Only attach while open; trigger click handles its own toggle
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !listRef.current?.contains(t)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const dropdownSpring = useSpring({
    opacity: open ? 1 : 0,
    transform: open ? "translateY(0px) scale(1)" : "translateY(-6px) scale(0.97)",
    config: { tension: 500, friction: 30, clamp: true },
  });

  const chevronSpring = useSpring({
    rotate: open ? 180 : 0,
    config: { tension: 500, friction: 30 },
  });

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={handleTriggerClick}
        className={[
          "flex w-full items-center justify-between rounded-lg border bg-muted/30 px-3 py-2 text-xs outline-none transition-colors",
          open ? "border-primary/60 ring-1 ring-primary/20" : "border-border hover:border-border/80",
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
          mono ? "font-mono" : "",
        ].join(" ")}
      >
        <span className={selected ? "truncate" : "truncate text-muted-foreground/50"}>
          {selected?.label ?? placeholder ?? "Select…"}
        </span>
        <animated.span style={{ rotate: chevronSpring.rotate }} className="ml-2 shrink-0">
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </animated.span>
      </button>

      {rect &&
        createPortal(
          <animated.div
            ref={listRef}
            style={{
              ...dropdownSpring,
              pointerEvents: open ? "auto" : "none",
              position: "fixed",
              top: rect.bottom + 4,
              left: rect.left,
              width: rect.width,
              zIndex: 9999,
            }}
            className="max-h-52 overflow-y-auto rounded-lg border border-border bg-popover py-1 shadow-[0_8px_32px_rgba(0,0,0,0.45)]"
          >
            {options.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleSelect(opt.value)}
                  className={[
                    "flex w-full items-center justify-between px-3 py-2 text-left transition-colors hover:bg-muted/40",
                    mono ? "font-mono text-xs" : "text-xs",
                    isSelected ? "text-primary" : "text-foreground",
                  ].join(" ")}
                >
                  <span className="truncate">{opt.label}</span>
                  {isSelected && <Check className="ml-2 h-3 w-3 shrink-0 text-primary" />}
                </button>
              );
            })}
          </animated.div>,
          document.body,
        )}
    </>
  );
}
